import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useSEO from '../../hooks/useSEO';
import {
  ITEM_GROUPS, OFFER_DAYS, OFFER_MEALS, Offer, OfferDay, OfferItem, OfferMeal, OfferPlan, OfferQuote, OfferSchedule,
  buildDailyFulfilment, isMainMeal, offersApi, scheduleDays, scheduleTotal, titleCase, formatPrice } from '../../services/offersApi';
import { useOfferData } from './OfferPage';
import { getStoredCampaignAttribution } from '../../hooks/useCampaignAttribution';
import { ItemThumb, OfferDraft, OfferShell, OfferState, Stepper, loadDraft, saveDraft } from './OfferShell';

const MAX_QTY = 10;
const STEPS = ['Meals', 'Delivery', 'Your details'];

const cloneSchedule = (schedule: OfferSchedule): OfferSchedule => {
  const out: OfferSchedule = {};
  OFFER_DAYS.forEach((day) => {
    const d = schedule[day];
    if (!d) return;
    out[day] = {};
    OFFER_MEALS.forEach((meal) => {
      if (d[meal]?.length) out[day]![meal] = d[meal]!.map((slot) => ({ itemId: slot.itemId, quantity: Math.max(1, slot.quantity || 1) }));
    });
  });
  return out;
};

const freshDraft = (slug: string, plan: OfferPlan): OfferDraft => ({
  slug,
  planId: plan._id,
  schedule: cloneSchedule(plan.schedule),
  fulfilment: 'delivery',
  customer: { name: '', email: '', phone: '', address: '' },
  note: '',
});

export default function OfferOrderPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const { data, error, retry } = useOfferData(slug);
  const planId = params.get('plan');

  useSEO({ title: data ? `Build your order - ${data.offer.name}` : 'Build your order - BOQBOX', description: 'Customise your meal plan and pay securely.' });

  if (error) {
    return (
      <OfferShell>
        <OfferState icon="⚠️" title="We couldn’t load this offer" message={error.message} action={<button className="mo-btn mo-btn-primary" onClick={retry}>Try again</button>} />
      </OfferShell>
    );
  }
  if (!data) {
    return (
      <OfferShell>
        <div className="mo-container mo-section" style={{ display: 'grid', gap: 14 }}>
          {[0, 1, 2].map((i) => <div key={i} className="mo-skeleton" style={{ height: 120 }} />)}
        </div>
      </OfferShell>
    );
  }
  const plan = data.plans.find((p) => p._id === planId);
  if (!plan) return <Navigate to={`/offers/${encodeURIComponent(data.offer.slug)}`} replace />;

  return <OrderBuilder offer={data.offer} menuList={data.menu} plan={plan} />;
}

function OrderBuilder({ offer, menuList, plan }: { offer: Offer; menuList: OfferItem[]; plan: OfferPlan }) {
  const navigate = useNavigate();
  const menu = useMemo(() => new Map(menuList.map((item) => [item._id, item])), [menuList]);
  const offerUrl = `/offers/${encodeURIComponent(offer.slug)}`;

  const [draft, setDraft] = useState<OfferDraft>(() => {
    const saved = loadDraft(offer.slug);
    return saved && saved.planId === plan._id ? { ...freshDraft(offer.slug, plan), ...saved, reference: undefined } : freshDraft(offer.slug, plan);
  });
  const [step, setStep] = useState(0);
  const [openDay, setOpenDay] = useState<OfferDay | null>(() => scheduleDays(plan.schedule)[0] ?? null);
  const [picker, setPicker] = useState<{ day: OfferDay; meal: OfferMeal } | null>(null);
  const [quote, setQuote] = useState<OfferQuote | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => { saveDraft(draft); }, [draft]);

  const planDays = scheduleDays(plan.schedule);
  const days = OFFER_DAYS.filter((day) => planDays.includes(day) || scheduleDays(draft.schedule).includes(day));
  const pickupEnabled = offer.pickupEnabled !== false;
  const fulfilment = useMemo(() => buildDailyFulfilment(offer, draft.schedule, draft.fulfilment), [offer, draft.schedule, draft.fulfilment]);
  const deliveredDays = fulfilment.filter((entry) => OFFER_MEALS.some((meal) => entry[meal]?.mode === 'hotel_delivery')).map((e) => e.day);
  const needsAddress = deliveredDays.length > 0;
  const hasMeals = scheduleDays(draft.schedule).length > 0;

  // Live, server-authoritative pricing (debounced).
  useEffect(() => {
    if (!hasMeals) { setQuote(null); return undefined; }
    setQuoting(true);
    const timer = window.setTimeout(async () => {
      try {
        const q = await offersApi.quote({
          campaign: offer._id,
          plan_id: plan._id,
          schedule: draft.schedule,
          fulfilment_preference: draft.fulfilment === 'pickup' ? 'all_pickup' : 'all_delivery',
          daily_fulfilment: fulfilment,
        });
        setQuote(q);
        setQuoteError(null);
      } catch (e) {
        setQuoteError((e as Error).message);
      } finally {
        setQuoting(false);
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [offer._id, plan._id, draft.schedule, draft.fulfilment, fulfilment, hasMeals]);

  // Local estimate shown until the quote arrives.
  const localSelected = scheduleTotal(draft.schedule, menu);
  const localMinimum = scheduleTotal(plan.schedule, menu) || plan.cost;
  const deliveredMeals = fulfilment.reduce((n, e) => n + OFFER_MEALS.filter((m) => e[m]?.mode === 'hotel_delivery').length, 0);
  const estimate = {
    planMinimum: localMinimum,
    selectedTotal: localSelected,
    mealSubtotal: Math.max(localMinimum, localSelected),
    deliverySubtotal: deliveredMeals * (offer.deliveryFeePerMeal ?? 400),
  };
  const shown = quote ?? { ...estimate, charges: undefined as number | undefined, totalAmount: undefined as number | undefined };
  const belowMinimum = shown.selectedTotal < shown.planMinimum;

  const updateSlot = (day: OfferDay, meal: OfferMeal, updater: (slots: NonNullable<NonNullable<OfferSchedule[OfferDay]>[OfferMeal]>) => NonNullable<NonNullable<OfferSchedule[OfferDay]>[OfferMeal]>) => {
    setDraft((d) => {
      const schedule = cloneSchedule(d.schedule);
      const dayObj = schedule[day] || {};
      const next = updater(dayObj[meal] ? [...dayObj[meal]!] : []);
      if (next.length) dayObj[meal] = next;
      else delete dayObj[meal];
      if (Object.keys(dayObj).length) schedule[day] = dayObj;
      else delete schedule[day];
      return { ...d, schedule };
    });
  };

  const addItem = (day: OfferDay, meal: OfferMeal, item: OfferItem) =>
    updateSlot(day, meal, (slots) => {
      const existing = slots.find((s) => s.itemId === item._id);
      if (existing) return slots.map((s) => (s.itemId === item._id ? { ...s, quantity: Math.min(MAX_QTY, s.quantity + 1) } : s));
      return [...slots, { itemId: item._id, quantity: 1 }];
    });

  const resetToPlan = () => setDraft((d) => ({ ...d, schedule: cloneSchedule(plan.schedule) }));

  const goTo = (next: number) => {
    setStep(next);
    setTouched(false);
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const c = draft.customer;
  const fieldErrors = {
    name: !c.name.trim() ? 'Enter your full name' : '',
    email: !/^\S+@\S+\.\S+$/.test(c.email.trim()) ? 'Enter a valid email address' : '',
    phone: c.phone.replace(/\D/g, '').length < 10 ? 'Enter a valid phone number' : '',
    address: needsAddress && c.address.trim().length < 6 ? 'Enter the delivery address' : '',
  };
  const detailsValid = !Object.values(fieldErrors).some(Boolean);
  const setCustomer = (key: keyof OfferDraft['customer'], value: string) => setDraft((d) => ({ ...d, customer: { ...d.customer, [key]: value } }));

  const pay = async () => {
    setTouched(true);
    if (!detailsValid) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const { paymentUrl, reference } = await offersApi.createOrder({
        campaign: offer._id,
        plan_id: plan._id,
        schedule: draft.schedule,
        fulfilment_preference: draft.fulfilment === 'pickup' ? 'all_pickup' : 'all_delivery',
        daily_fulfilment: fulfilment,
        customer_name: c.name.trim(),
        customer_email: c.email.trim(),
        customer_phone: c.phone.trim(),
        customer_address: needsAddress ? c.address.trim() : undefined,
        note: draft.note.trim() || undefined,
        referral_code: getStoredCampaignAttribution(offer.slug)?.referralCode,
        callback_url: `${window.location.origin}${offerUrl}/confirmation`,
      });
      saveDraft({ ...draft, reference, quotedTotal: quote?.totalAmount });
      window.location.href = paymentUrl;
    } catch (e) {
      setSubmitError((e as Error).message);
      setSubmitting(false);
    }
  };

  const primary = (() => {
    if (step === 0) return { label: 'Continue', disabled: !hasMeals, onClick: () => goTo(1) };
    if (step === 1) return { label: 'Continue', disabled: !hasMeals, onClick: () => goTo(2) };
    return { label: submitting ? 'Starting payment…' : 'Pay securely', disabled: submitting || !hasMeals || Boolean(quoteError && !quote), onClick: pay };
  })();

  const totalLabel = shown.totalAmount !== undefined ? formatPrice(shown.totalAmount) : formatPrice(shown.mealSubtotal + shown.deliverySubtotal);

  const summary = (
    <aside className="mo-card mo-summary" aria-label="Order summary">
      <h3>{plan.name} plan</h3>
      <div className="mo-sum-row"><span>Days</span><strong>{scheduleDays(draft.schedule).map((d) => titleCase(d.slice(0, 3))).join(', ') || '—'}</strong></div>
      <div className="mo-sum-row"><span>Your meals</span><strong>{formatPrice(shown.selectedTotal)}</strong></div>
      {belowMinimum ? (
        <div className="mo-note">This plan has a minimum of <strong>{formatPrice(shown.planMinimum)}</strong>. You can add {formatPrice(shown.planMinimum - shown.selectedTotal)} more in meals at no extra cost.</div>
      ) : null}
      <div className="mo-sum-row"><span>Meals subtotal</span><strong>{formatPrice(shown.mealSubtotal)}</strong></div>
      <div className="mo-sum-row"><span>Delivery{deliveredMeals ? ` (${deliveredMeals} meal${deliveredMeals === 1 ? '' : 's'})` : ''}</span><strong>{shown.deliverySubtotal ? formatPrice(shown.deliverySubtotal) : 'Free'}</strong></div>
      <div className="mo-sum-row"><span>Service & payment fees</span><strong>{shown.charges !== undefined ? formatPrice(shown.charges) : '…'}</strong></div>
      <div className="mo-sum-total"><span>Total</span><span>{quoting && !quote ? <span className="mo-spinner" /> : totalLabel}</span></div>
      {quoteError ? <div className="mo-error">{quoteError}</div> : null}
      <button className="mo-btn mo-btn-primary mo-btn-block" disabled={primary.disabled} onClick={primary.onClick}>
        {submitting ? <span className="mo-spinner" /> : null}{primary.label}
      </button>
      {step === 2 ? <div style={{ fontSize: 12.5, color: 'var(--mo-ink-3)', textAlign: 'center' }}>🔒 Secured by Paystack</div> : null}
    </aside>
  );

  return (
    <OfferShell backTo={offerUrl} backLabel="Back to offer">
      <div className="mo-container" ref={topRef}>
        <div className="mo-order-head">
          <button className="mo-back" type="button" onClick={() => (step ? goTo(step - 1) : navigate(offerUrl))}>← {step ? STEPS[step - 1] : offer.name}</button>
          <h1>{STEPS[step] === 'Meals' ? 'Customise your meals' : STEPS[step] === 'Delivery' ? 'How should we get it to you?' : 'Almost done'}</h1>
          <p>{offer.name} · {plan.name} plan</p>
        </div>
        <div className="mo-steps" role="tablist" aria-label="Order steps">
          {STEPS.map((label, i) => (
            <button key={label} type="button" role="tab" aria-selected={i === step} className={`mo-step${i < step ? ' mo-step-done' : ''}${i === step ? ' mo-step-active' : ''}`} disabled={i > step} onClick={() => goTo(i)}>
              {i + 1}. {label}
            </button>
          ))}
        </div>

        <div className="mo-layout">
          <div>
            {step === 0 ? (
              <>
                {days.map((day) => {
                  const dayMeals = OFFER_MEALS.filter((meal) => (plan.schedule[day]?.[meal] || []).length || (draft.schedule[day]?.[meal] || []).length);
                  const daySchedule = { [day]: draft.schedule[day] } as OfferSchedule;
                  const dayTotal = scheduleTotal(daySchedule, menu);
                  const isOpen = openDay === day;
                  return (
                    <div key={day} className={`mo-card mo-day${isOpen ? ' mo-open' : ''}`}>
                      <button type="button" className="mo-day-head" aria-expanded={isOpen} onClick={() => setOpenDay(isOpen ? null : day)}>
                        <strong>{day}</strong>
                        <span>{dayTotal ? formatPrice(dayTotal) : 'No meals'} <span className="mo-chev" aria-hidden="true">▾</span></span>
                      </button>
                      {isOpen ? (
                        <div className="mo-day-body">
                          {dayMeals.map((meal) => {
                            const slots = draft.schedule[day]?.[meal] || [];
                            return (
                              <div key={meal} className="mo-slot">
                                <div className="mo-slot-title">{meal}</div>
                                {slots.length === 0 ? <div style={{ fontSize: 13.5, color: 'var(--mo-ink-3)', padding: '4px 0' }}>Nothing selected for {meal}.</div> : null}
                                {slots.map((slot) => {
                                  const item = menu.get(slot.itemId);
                                  return (
                                    <div key={slot.itemId} className="mo-line-item">
                                      <ItemThumb item={item} />
                                      <div style={{ minWidth: 0 }}>
                                        <strong>{item?.name || 'Unavailable item'}</strong>
                                        <small>{item ? formatPrice(item.price) : ''}{slot.quantity > 1 && item ? ` × ${slot.quantity} = ${formatPrice(item.price * slot.quantity)}` : ''}</small>
                                      </div>
                                      <Stepper
                                        label={item?.name || 'item'}
                                        value={slot.quantity}
                                        max={MAX_QTY}
                                        onChange={(next) => updateSlot(day, meal, (list) => (next <= 0 ? list.filter((s) => s.itemId !== slot.itemId) : list.map((s) => (s.itemId === slot.itemId ? { ...s, quantity: next } : s))))}
                                      />
                                    </div>
                                  );
                                })}
                                <button type="button" className="mo-add" onClick={() => setPicker({ day, meal })}>＋ Add to {meal}</button>
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
                <button type="button" className="mo-btn mo-btn-ghost mo-btn-sm" onClick={resetToPlan}>Reset to the {plan.name} plan</button>
              </>
            ) : null}

            {step === 1 ? (
              <div className="mo-card mo-panel">
                <h2>Delivery or pickup</h2>
                <p>Delivery is {formatPrice(offer.deliveryFeePerMeal ?? 400)} per meal.{pickupEnabled ? ' Pickup is free.' : ''}</p>
                <div className="mo-options">
                  <button type="button" className={`mo-option${draft.fulfilment === 'delivery' ? ' mo-option-on' : ''}`} aria-pressed={draft.fulfilment === 'delivery'} onClick={() => setDraft((d) => ({ ...d, fulfilment: 'delivery' }))}>
                    <span className="mo-option-icon" aria-hidden="true">🛵</span>
                    <span><strong>Deliver my meals</strong><span>Hot meals brought to your address for every meal.</span></span>
                  </button>
                  <button type="button" disabled={!pickupEnabled} className={`mo-option${draft.fulfilment === 'pickup' ? ' mo-option-on' : ''}`} aria-pressed={draft.fulfilment === 'pickup'} onClick={() => setDraft((d) => ({ ...d, fulfilment: 'pickup' }))}>
                    <span className="mo-option-icon" aria-hidden="true">🏪</span>
                    <span><strong>I’ll pick up</strong><span>{pickupEnabled ? `Free. Available from ${titleCase(offer.standPickupAvailableFrom || 'thursday')}.` : 'Not available for this offer.'}</span></span>
                  </button>
                </div>
                {draft.fulfilment === 'pickup' && deliveredDays.length ? (
                  <div className="mo-note" style={{ marginTop: 14 }}>
                    Pickup starts on {titleCase(offer.standPickupAvailableFrom || 'thursday')}, so your meals on {deliveredDays.map(titleCase).join(', ')} will be delivered.
                  </div>
                ) : null}
                {draft.fulfilment === 'pickup' && offer.pickupInstruction ? <div className="mo-note mo-note-info" style={{ marginTop: 14 }}>📍 {offer.pickupInstruction}</div> : null}
              </div>
            ) : null}

            {step === 2 ? (
              <div className="mo-card mo-panel">
                <h2>Your details</h2>
                <p>We’ll send your receipt and updates here.</p>
                <div className="mo-form mo-form-2">
                  <Field label="Full name" error={touched ? fieldErrors.name : ''}>
                    <input autoComplete="name" value={c.name} onChange={(e) => setCustomer('name', e.target.value)} />
                  </Field>
                  <Field label="Phone number" error={touched ? fieldErrors.phone : ''}>
                    <input type="tel" inputMode="tel" autoComplete="tel" value={c.phone} onChange={(e) => setCustomer('phone', e.target.value)} />
                  </Field>
                  <Field label="Email" error={touched ? fieldErrors.email : ''} span>
                    <input type="email" inputMode="email" autoComplete="email" value={c.email} onChange={(e) => setCustomer('email', e.target.value)} />
                  </Field>
                  {needsAddress ? (
                    <Field label="Delivery address" error={touched ? fieldErrors.address : ''} span>
                      <textarea autoComplete="street-address" rows={2} placeholder="Hotel / house name, street, area, city" value={c.address} onChange={(e) => setCustomer('address', e.target.value)} />
                    </Field>
                  ) : null}
                  <Field label="Note for the kitchen (optional)" span>
                    <textarea rows={2} placeholder="Allergies, delivery instructions…" value={draft.note} onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))} />
                  </Field>
                </div>
                {submitError ? <div className="mo-error" style={{ marginTop: 14 }}>{submitError}</div> : null}
              </div>
            ) : null}

            <div className="mo-bar">
              <div className="mo-bar-total">
                <small>{shown.totalAmount !== undefined ? 'Total' : 'Estimated total'}</small>
                <strong>{totalLabel}</strong>
              </div>
              <button className="mo-btn mo-btn-primary" disabled={primary.disabled} onClick={primary.onClick}>
                {submitting ? <span className="mo-spinner" /> : null}{primary.label}
              </button>
            </div>
          </div>
          <div className="mo-summary-wrap">{summary}</div>
        </div>
      </div>

      {picker ? (
        <ItemPicker
          menuList={menuList}
          meal={picker.meal}
          day={picker.day}
          onPick={(item) => addItem(picker.day, picker.meal, item)}
          onClose={() => setPicker(null)}
        />
      ) : null}
    </OfferShell>
  );
}

function Field({ label, error, span, children }: { label: string; error?: string; span?: boolean; children: React.ReactElement }) {
  const id = `mo-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`;
  return (
    <div className={`mo-field${span ? ' mo-span-2' : ''}`}>
      <label htmlFor={id}>{label}</label>
      {React.cloneElement(children, { id, 'aria-invalid': Boolean(error) || undefined })}
      {error ? <span className="mo-field-error">{error}</span> : null}
    </div>
  );
}

function ItemPicker({ menuList, meal, day, onPick, onClose }: { menuList: OfferItem[]; meal: OfferMeal; day: OfferDay; onPick: (item: OfferItem) => void; onClose: () => void }) {
  const [added, setAdded] = useState<string | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = previous; };
  }, [onClose]);

  // Meals for this slot first, then other meals, drinks and extras.
  const groups = ITEM_GROUPS.map((group) => ({
    ...group,
    items: menuList
      .filter((item) => group.types.includes(item.type))
      .sort((a, b) => Number(isMainMeal(b.type) && b.type === meal) - Number(isMainMeal(a.type) && a.type === meal) || a.price - b.price),
  })).filter((group) => group.items.length);

  return (
    <>
      <div className="mo-sheet-backdrop" onClick={onClose} />
      <div className="mo-sheet" role="dialog" aria-modal="true" aria-label={`Add to ${day} ${meal}`}>
        <div className="mo-sheet-grip" />
        <div className="mo-sheet-head">
          <h3>Add to {titleCase(day)} {meal}</h3>
          <button className="mo-sheet-close" type="button" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="mo-sheet-body">
          {groups.length === 0 ? <p style={{ color: 'var(--mo-ink-2)' }}>The menu is empty.</p> : null}
          {groups.map((group) => (
            <div key={group.key}>
              <div className="mo-sheet-group">{group.label}</div>
              {group.items.map((item) => (
                <button key={item._id} type="button" className="mo-pick" onClick={() => { onPick(item); setAdded(item._id); window.setTimeout(() => setAdded(null), 900); }}>
                  <ItemThumb item={item} />
                  <span style={{ minWidth: 0 }}>
                    <strong>{item.name}</strong>
                    <small>{formatPrice(item.price)}{isMainMeal(item.type) && item.type !== meal ? ` · ${titleCase(item.type)} menu` : ''}</small>
                  </span>
                  <span className="mo-pick-add" aria-hidden="true">{added === item._id ? '✓' : '+'}</span>
                </button>
              ))}
            </div>
          ))}
          <button type="button" className="mo-btn mo-btn-primary mo-btn-block" style={{ marginTop: 14 }} onClick={onClose}>Done</button>
        </div>
      </div>
    </>
  );
}

